// components/QuickAddEquipmentModal.jsx - Quick Add Equipment Modal
import React, { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import * as api from '../services/api'

const COMMON_EQUIPMENT = [
  'Backpack Sprayer',
  'Handheld Sprayer',
  'Truck-mounted Sprayer',
  'Arborjet QUIK-jet Air',
  'Arborjet TREE I.V.',
  'Mauget Injector',
  'Soil Injection Probe',
  'Broadcast Spreader',
  'Drop Spreader',
  'Hose-end Sprayer'
]

export default function QuickAddEquipmentModal({ shortCode, onAdd, onClose }) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [name, setName] = useState('')
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!name.trim()) {
      setError('Equipment name is required')
      return
    }
    
    setSaving(true)
    setError(null)
    
    try {
      const result = await api.addEquipment(shortCode, name.trim())
      onAdd(result.equipment || { name: name.trim() })
    } catch (err) {
      setError(err.message || 'Failed to add equipment')
      setSaving(false)
    }
  }
  
  const selectCommon = (equipmentName) => {
    setName(equipmentName)
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl 
                      max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Add Equipment</h3>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          
          <div>
            <label className="form-label">
              Equipment Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., 4-gallon Backpack Sprayer"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input"
              autoFocus
            />
          </div>
          
          {/* Common equipment suggestions */}
          <div>
            <label className="form-label">Common Equipment</label>
            <div className="flex flex-wrap gap-2">
              {COMMON_EQUIPMENT.map((eq) => (
                <button
                  key={eq}
                  type="button"
                  onClick={() => selectCommon(eq)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors
                    ${name === eq 
                      ? 'bg-arbor-500 text-white border-arbor-500' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-arbor-400'
                    }`}
                >
                  {eq}
                </button>
              ))}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Equipment'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
